import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthHeader } from '../../services/tokenService';
import { mapEffectifToOption } from './eligibilityHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useEligibilityProfile({ membre, onEligibilityChange, onValidityChange, nombreActivites = 0, isSuperAdmin = false, territoiresCAInitial = null }) {
  const [collapsed, setCollapsed] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [experienceAnnees, setExperienceAnnees] = useState('');
  const [ancienneteAnnees, setAncienneteAnnees] = useState('');
  const [effectif, setEffectif] = useState('');
  const [qualifications, setQualifications] = useState([]);
  const [typeMarche, setTypeMarche] = useState('');
  const [territoiresCA, setTerritoiresCA] = useState({});
  const [ventClientele, setVentClientele] = useState({});
  const [ventContrat, setVentContrat] = useState({});
  const [ventTravaux, setVentTravaux] = useState({});

  // Prévention & Documentation (Couche 4 — moteur d'éligibilité)
  const [accidentsTravail, setAccidentsTravail] = useState(false);
  const [accidentsList, setAccidentsList] = useState([]); // [{date, causes, circonstances, cout_estime}]
  const [sinistresAnterieurs, setSinistresAnterieurs] = useState(false);
  const [sinistralitDate, setSinistralitDate] = useState(''); // date du relevé
  const [sinistralitFiles, setSinistralitFiles] = useState([]); // {file, status, doc_id?, error?}
  const [pvReception, setPvReception] = useState(false);
  const [pvReceptionFiles, setPvReceptionFiles] = useState([]); // {file, status, doc_id?, error?}
  const [referencesTravauxFiles, setReferencesTravauxFiles] = useState([]); // idem

  // Charger la config au mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/batico/eligibility/config`, { headers: getAuthHeader() });
        setConfig(res.data);
      } catch (e) {
        console.error('Erreur chargement config éligibilité:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Pré-remplir depuis le profil du membre (one-shot) :
  // - Ancienneté : depuis date_creation_entreprise (champ canonique) ou
  //   date_creation (legacy), gère les formats ISO et JJ/MM/AAAA.
  // - Effectif : converti depuis un nombre brut (4 → "1-5 salariés") ou
  //   gardé tel quel si déjà au format option.
  // - Expérience professionnelle : repris depuis le profil membre si dispo.
  // - Qualifications : utilisées telles quelles si présentes sur le membre.
  // Tracking via ref pour ne prefill QU'UNE seule fois par identité de membre
  // (évite d'écraser un changement utilisateur ultérieur).
  const prefilledForMembreRef = useRef(null);
  useEffect(() => {
    // Attendre que la config soit chargée (sinon le formulaire n'est pas
    // encore monté et l'admin user peut « consommer » le ref par erreur).
    if (loading) return;
    const membreKey = membre?.id || membre?.user_id || membre?.membre_id || null;
    if (!membreKey) return;
    // V35.55 — Ne consommer le ref one-shot QUE si le membre a au moins UN
    // champ RH réellement utile (effectif / date / expérience). Sinon, on
    // attend la prochaine évolution de la prop (typiquement le fetch async
    // /admin/membres/{id} qui ramène les vraies données). Sans cette
    // restriction, les params URL (qui n'ont que raison_sociale/siret/nom)
    // consommaient le ref AVANT l'arrivée des données RH.
    const hasMembreHrData = !!(
      membre?.effectif
      || membre?.date_creation_entreprise
      || membre?.date_creation
      || membre?.experience_professionnelle_annees != null
      || membre?.experience_pro_annees != null
      || membre?.experience_annees != null
      || membre?.experience_pro != null
      || membre?.chiffre_affaires
      || (Array.isArray(membre?.qualifications) && membre.qualifications.length > 0)
    );
    if (!hasMembreHrData) return;
    // V38.01 — signature basée sur les VALEURS RH : si le profil membre est
    // complété pendant la session (modale « Compléter le profil »), l'effet
    // se ré-arme et préremplit les champs encore vides (jamais d'écrasement :
    // chaque set ci-dessous est gardé par un test « champ vide »).
    const membreSig = [
      membreKey,
      membre?.effectif ?? '',
      membre?.date_creation_entreprise || membre?.date_creation || '',
      membre?.experience_professionnelle_annees ?? membre?.experience_pro_annees ?? membre?.experience_annees ?? membre?.experience_pro ?? '',
      membre?.chiffre_affaires ?? '',
      membre?.territoire ?? '',
    ].join('|');
    if (prefilledForMembreRef.current === membreSig) return; // déjà prefillé pour cet état
    prefilledForMembreRef.current = membreSig;

    // Ancienneté entreprise (depuis date_creation_entreprise / date_creation)
    const rawDate = membre?.date_creation_entreprise || membre?.date_creation;
    if (rawDate && !ancienneteAnnees) {
      let parsed = null;
      const isoMatch = String(rawDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const frMatch = String(rawDate).match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
      if (isoMatch) {
        parsed = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
      } else if (frMatch) {
        parsed = new Date(`${frMatch[3]}-${frMatch[2]}-${frMatch[1]}`);
      } else {
        parsed = new Date(rawDate);
      }
      if (parsed && !isNaN(parsed.getTime())) {
        const years = Math.floor((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
        if (years >= 0) setAncienneteAnnees(String(years));
      }
    }

    // Effectif : convertir un nombre brut (4) vers une option valide
    // ("1-5 salariés"). Si le membre a déjà une option valide on la garde.
    const mappedEffectif = mapEffectifToOption(membre?.effectif);
    if (mappedEffectif) {
      setEffectif(mappedEffectif);
    }

    // Expérience professionnelle (plusieurs alias possibles)
    const expRaw = membre?.experience_professionnelle_annees
      ?? membre?.experience_pro_annees
      ?? membre?.experience_annees
      ?? membre?.experience_pro;
    if (expRaw !== null && expRaw !== undefined && expRaw !== '' && experienceAnnees === '') {
      const n = parseInt(String(expRaw).match(/-?\d+/)?.[0] ?? '', 10);
      if (Number.isFinite(n) && n >= 0) setExperienceAnnees(String(n));
    }

    // Qualifications
    if (Array.isArray(membre?.qualifications) && qualifications.length === 0) {
      setQualifications(membre.qualifications);
    }

    // V38.00 — Chiffre d'affaires : pré-rempli depuis le profil membre
    // (champ chiffre_affaires du tableau des membres), affecté à 100 % sur
    // le territoire du membre (modifiable ensuite par l'utilisateur).
    const caNum = parseFloat(
      String(membre?.chiffre_affaires ?? '').replace(/[^\d.,-]/g, '').replace(',', '.')
    );
    if (
      Number.isFinite(caNum) && caNum > 0
      && Object.keys(territoiresCA).length === 0
      && Array.isArray(config?.territoires) && config.territoires.length > 0
    ) {
      const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      const target = norm(membre?.territoire);
      const match = config.territoires.find((t) => norm(t.key) === target || norm(t.label) === target);
      const key = (match || config.territoires[0]).key;
      setTerritoiresCA({ [key]: String(Math.round(caNum)) });
    }
  // Volontairement déclenché à chaque changement d'identité membre.
  // eslint-disable-next-line
  }, [loading, membre?.id, membre?.user_id, membre?.membre_id, membre?.effectif, membre?.experience_professionnelle_annees, membre?.date_creation_entreprise, membre?.date_creation, membre?.chiffre_affaires, membre?.raison_sociale, membre?.siret]);

  // Avenant : répartition du CA par territoire reprise du contrat parent.
  const tcaCle = territoiresCAInitial ? JSON.stringify(territoiresCAInitial) : '';
  useEffect(() => {
    if (!tcaCle || !Array.isArray(config?.territoires) || !config.territoires.length) return;
    const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');
    const repris = {};
    Object.entries(JSON.parse(tcaCle)).forEach(([k, v]) => {
      const n = Number(v);
      if (!(n > 0)) return;
      const t = config.territoires.find((x) => norm(x.key) === norm(k) || norm(x.label) === norm(k));
      const cle = t ? t.key : k;
      repris[cle] = String(Math.round((Number(repris[cle]) || 0) + n));
    });
    if (Object.keys(repris).length) setTerritoiresCA(repris);
  }, [tcaCle, config]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback : si le prefill n'a pas fixé d'effectif (pas de membre, ou
  // membre sans champ effectif), on garde la valeur par défaut sensible
  // « 1-5 salariés » pour ne pas laisser le `<select>` désynchronisé du state.
  useEffect(() => {
    if (loading) return;
    if (effectif === '') setEffectif('1-5 salariés');
  // eslint-disable-next-line
  }, [loading]);


  // === Auto-fill : si le membre a déjà des documents dans la GED/fiche membre,
  // on pré-coche les sections correspondantes et on affiche un récap des
  // fichiers existants. Évite à l'utilisateur de re-uploader. ===
  // Étend également la pré-sélection aux qualifications validées (status
  // === 'approved') si le membre n'a pas de tableau `qualifications` explicite
  // sur son profil.
  useEffect(() => {
    if (!membre?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/admin/membres/${membre.id}/qualifications`,
          { headers: getAuthHeader() },
        );
        if (cancelled) return;
        const items = res.data?.qualifications || [];
        // Clés "virtuelles" non considérées comme qualifications professionnelles
        // (cf. backend membre_qualifications.VIRTUAL_QUALIF_LABELS)
        const VIRTUAL_KEYS = new Set(['pv_reception', 'references_travaux', 'releve_sinistralite']);
        // Pré-sélectionner toutes les qualifications validées (approved) si le
        // membre n'a pas déjà fourni un tableau explicite.
        const approvedKeys = items
          .filter((it) => it.status === 'approved' && !VIRTUAL_KEYS.has(it.key))
          .map((it) => it.key);
        if (approvedKeys.length > 0 && qualifications.length === 0) {
          setQualifications(approvedKeys);
        }

        const buildExistingFiles = (docs) => (docs || []).map((d) => ({
          name: d.original_filename || d.filename,
          status: d.status === 'approved' ? 'uploaded' : (d.status === 'rejected' ? 'error' : 'uploading'),
          doc_id: d.id,
          existing: true}));
        items.forEach((it) => {
          const docs = it.documents || [];
          if (!docs.length) return;
          if (it.key === 'pv_reception') {
            setPvReception(true);
            setPvReceptionFiles((curr) => curr.length ? curr : buildExistingFiles(docs));
          } else if (it.key === 'references_travaux') {
            setReferencesTravauxFiles((curr) => curr.length ? curr : buildExistingFiles(docs));
          } else if (it.key === 'releve_sinistralite') {
            setSinistresAnterieurs(true);
            setSinistralitFiles((curr) => curr.length ? curr : buildExistingFiles(docs));
            // Pré-remplir la date si elle est stockée comme valid_until
            const firstWithDate = docs.find((d) => d.valid_until);
            if (firstWithDate?.valid_until) {
              setSinistralitDate((curr) => curr || firstWithDate.valid_until);
            }
          }
        });
      } catch (err) {
        // Silencieux : non-admin (membre simple) recevra peut-être 403, c'est OK
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Auto-prefill documents skipped:', err?.response?.status);
        }
      }

      // === Auto-fill élargi : documents membres uploadés via des types
      // personnalisés liés à une rubrique d'éligibilité BATICO. ===
      // Le Super Admin peut mapper un type personnalisé (ex. "Diplôme RGE")
      // à une rubrique d'éligibilité (`qualification:<key>` ou virtuelle :
      // `pv_reception`, `references_travaux`, `releve_sinistralite`).
      // Tout document uploadé avec ce type → reconnu automatiquement.
      try {
        const [docsRes, typesRes] = await Promise.all([
          axios.get(`${API}/admin/membres/${membre.id}/documents`, { headers: getAuthHeader() }),
          axios.get(`${API}/admin/ged/document-types`, { headers: getAuthHeader() }),
        ]);
        if (cancelled) return;
        const memberDocs = docsRes.data?.documents || [];
        const customTypes = typesRes.data || [];
        // Map { type_value → eligibility_rubrique }
        const typeRubriqueMap = new Map(
          customTypes
            .filter((t) => t.eligibility_rubrique)
            .map((t) => [t.value, t.eligibility_rubrique])
        );
        if (typeRubriqueMap.size === 0) return;

        // Collecte des rubriques détectées et des fichiers correspondants
        const detectedQualifs = new Set();
        const detectedPvFiles = [];
        const detectedRefsFiles = [];
        const detectedSinistraliteFiles = [];

        memberDocs.forEach((doc) => {
          const rubrique = typeRubriqueMap.get(doc.type_document);
          if (!rubrique) return;
          const fileEntry = {
            name: doc.original_filename || doc.filename || doc.type_document_label || 'Document',
            status: 'uploaded',
            doc_id: doc.id,
            existing: true,
            from_custom_type: true};
          if (rubrique.startsWith('qualification:')) {
            const key = rubrique.slice('qualification:'.length);
            if (key) detectedQualifs.add(key);
          } else if (rubrique === 'pv_reception') {
            detectedPvFiles.push(fileEntry);
          } else if (rubrique === 'references_travaux') {
            detectedRefsFiles.push(fileEntry);
          } else if (rubrique === 'releve_sinistralite') {
            detectedSinistraliteFiles.push(fileEntry);
          }
        });

        // Application des résultats — sans écraser un choix manuel utilisateur
        if (detectedQualifs.size > 0) {
          setQualifications((prev) => {
            const merged = new Set([...prev, ...detectedQualifs]);
            return Array.from(merged);
          });
        }
        if (detectedPvFiles.length > 0) {
          setPvReception(true);
          setPvReceptionFiles((curr) => curr.length ? curr : detectedPvFiles);
        }
        if (detectedRefsFiles.length > 0) {
          setReferencesTravauxFiles((curr) => curr.length ? curr : detectedRefsFiles);
        }
        if (detectedSinistraliteFiles.length > 0) {
          setSinistresAnterieurs(true);
          setSinistralitFiles((curr) => curr.length ? curr : detectedSinistraliteFiles);
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Auto-fill custom types skipped:', err?.response?.status);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [membre?.id]);

  // Notifier le parent dès qu'un champ change
  useEffect(() => {
    const formData = {
      experience_professionnelle_annees: experienceAnnees ? Number(experienceAnnees) : null,
      anciennete_annees: ancienneteAnnees ? Number(ancienneteAnnees) : null,
      effectif,
      qualifications,
      type_marche: typeMarche || null,
      territoires_ca: Object.fromEntries(
        Object.entries(territoiresCA).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      ),
      ventilation_clientele: Object.fromEntries(
        Object.entries(ventClientele).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      ),
      ventilation_contrat: Object.fromEntries(
        Object.entries(ventContrat).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      ),
      ventilation_travaux: Object.fromEntries(
        Object.entries(ventTravaux).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      ),
      // Prévention & Documentation (Couche 4 BATICO)
      accidents_travail_5ans: accidentsTravail,
      accidents_travail_list: accidentsTravail ? accidentsList : [],
      sinistres_anterieurs: sinistresAnterieurs,
      sinistralite_date: sinistresAnterieurs ? sinistralitDate : null,
      sinistralite_files_count: sinistralitFiles.length,
      pv_reception_present: pvReception,
      pv_reception_files_count: pvReceptionFiles.length,
      references_travaux_files_count: referencesTravauxFiles.length};
    onEligibilityChange?.(formData);

    // Validité : ventilations qui ont au moins une valeur doivent faire 100%
    const checkSum = (obj) => {
      const total = Object.values(obj).reduce((s, v) => s + (Number(v) || 0), 0);
      return total === 0 || Math.abs(total - 100) <= 1;
    };
    const valid = checkSum(ventClientele) && checkSum(ventContrat) && checkSum(ventTravaux);
    onValidityChange?.(valid);
  }, [experienceAnnees, ancienneteAnnees, effectif, qualifications, typeMarche, territoiresCA, ventClientele, ventContrat, ventTravaux, accidentsTravail, accidentsList, sinistresAnterieurs, sinistralitDate, sinistralitFiles.length, pvReception, pvReceptionFiles.length, referencesTravauxFiles.length, onEligibilityChange, onValidityChange]);

  // Auto-suggestion du type de marché basé sur le nombre d'activités cochées.
  // - 1 activité       → mono_activite
  // - 2 à 15 activités  → multi_activite_limitee
  // - > 15 activités    → plus_15_activites
  // L'auto-suggestion ne s'applique que si l'utilisateur n'a pas choisi un type
  // "spécial" (entreprise_generale, tous_corps_etat, cmi, promoteur, moe_bet_amo).
  // Si l'utilisateur sélectionne explicitement un de ces types spéciaux, on respecte son choix.
  const AUTO_TYPES = useMemo(() => ['mono_activite', 'multi_activite_limitee', 'plus_15_activites'], []);
  useEffect(() => {
    if (!nombreActivites || nombreActivites <= 0) return;
    let auto = null;
    if (nombreActivites === 1) auto = 'mono_activite';
    else if (nombreActivites <= 15) auto = 'multi_activite_limitee';
    else auto = 'plus_15_activites';

    // On n'écrase pas un type spécial choisi manuellement par l'utilisateur
    if (!typeMarche || AUTO_TYPES.includes(typeMarche)) {
      if (typeMarche !== auto) setTypeMarche(auto);
    }
  }, [nombreActivites, typeMarche, AUTO_TYPES]);

  const toggleQualif = (key) => {
    setQualifications((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const territoireCATotal = useMemo(
    () => Object.values(territoiresCA).reduce((s, v) => s + (Number(v) || 0), 0),
    [territoiresCA]
  );

  // Coût total des accidents déclarés (pour seuil moteur d'éligibilité)
  const accidentsCoutTotal = useMemo(
    () => accidentsList.reduce((s, a) => s + (Number(a?.cout_estime) || 0), 0),
    [accidentsList]
  );

  // Upload réel d'un justificatif via l'API Qualifications
  // (clés autorisées : pv_reception, references_travaux, releve_sinistralite)
  const uploadJustificatif = useCallback(async (file, qualifKey, setFiles, validUntil = null) => {
    if (!membre?.id) {
      toast.error("Pas d'identifiant membre — impossible d'uploader. Connectez-vous d'abord.");
      return;
    }
    const entry = { file, name: file.name, status: 'uploading', error: null, doc_id: null };
    setFiles((prev) => [...prev, entry]);
    const idx = (curr) => curr.findIndex((x) => x === entry || x.name === file.name && x.status === 'uploading');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (validUntil) fd.append('valid_until', validUntil);
      const res = await axios.post(
        `${API}/admin/membres/${membre.id}/qualifications/${qualifKey}/upload`,
        fd,
        {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }}
      );
      setFiles((curr) => {
        const i = idx(curr);
        if (i < 0) return curr;
        const next = [...curr];
        next[i] = { ...entry, status: 'uploaded', doc_id: res.data?.document?.id };
        return next;
      });
      toast.success(`${file.name} téléversé et enregistré dans la fiche membre + GED`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erreur upload';
      setFiles((curr) => {
        const i = idx(curr);
        if (i < 0) return curr;
        const next = [...curr];
        next[i] = { ...entry, status: 'error', error: detail };
        return next;
      });
      toast.error(`Échec ${file.name} : ${detail}`);
    }
  }, [membre?.id]);

  return {
    AUTO_TYPES, accidentsCoutTotal, accidentsList, accidentsTravail, ancienneteAnnees, collapsed,
    config, effectif, experienceAnnees, loading, prefilledForMembreRef, pvReception,
    pvReceptionFiles, qualifications, referencesTravauxFiles, setAccidentsList, setAccidentsTravail, setAncienneteAnnees,
    setCollapsed, setConfig, setEffectif, setExperienceAnnees, setLoading, setPvReception,
    setPvReceptionFiles, setQualifications, setReferencesTravauxFiles, setSinistralitDate, setSinistralitFiles, setSinistresAnterieurs,
    setTerritoiresCA, setTypeMarche, setVentClientele, setVentContrat, setVentTravaux, sinistralitDate,
    sinistralitFiles, sinistresAnterieurs, territoireCATotal, territoiresCA, toggleQualif, typeMarche,
    uploadJustificatif, ventClientele, ventContrat, ventTravaux, membre, isSuperAdmin,
    nombreActivites,
    onEligibilityChange, onValidityChange};
}
